import React from 'react'
import cn from 'classnames'
import UserIcon from 'components/users/UserIcon'
import { quoteSnippetForAnswer } from 'components/answers/quoteSnippet'
import { getCachedAnswer } from 'state/cache'
import crumbStyles from 'components/quests/Quest/thin-parent.module.scss'
import styles from './reply-modal-crumb.module.scss'
import type { Answer } from 'types/answers'
import type { Quest } from 'types/quests'

// A thread is named by its head post (quest.parent), hydrated from the cache
// when the inline copy is lean.
export function threadHeadPost(quest?: Quest | null): Answer | null {
    const id = quest?.parent?.id || quest?.parent_id
    const answer = ((id && getCachedAnswer(String(id))) || quest?.parent || null) as Answer | null
    return answer?.id ? answer : null
}

// The crumb already names its head post, so a reply to that post needs no
// quote under it as well.
export function quoteRepeatsCrumb(
    crumbAnswer?: Partial<Answer> | null,
    replyToAnswer?: Partial<Answer> | null,
): boolean {
    return (
        !!crumbAnswer?.id &&
        !!replyToAnswer?.id &&
        String(crumbAnswer.id) === String(replyToAnswer.id)
    )
}

interface ReplyModalCrumbProps {
    // The head post of the thread the reply was started from (threadHeadPost).
    answer?: Answer | null
    className?: string
}

// The thread a reply is going to, at the top of the reply modal, in the panel
// crumb's pill. A label only: the modal holds a draft, so nothing here
// navigates away from it.
const ReplyModalCrumb: React.FC<ReplyModalCrumbProps> = ({ answer, className }) => {
    const title = quoteSnippetForAnswer(answer)
    if (!title) return null

    return (
        <div className={cn(styles.replyModalCrumb, className)} data-testid="reply-modal-crumb">
            <span
                className={cn(styles.threadPill, !answer?.user && styles.threadPillWithoutAvatar)}
                title={title}
            >
                {answer?.user && (
                    <span className={crumbStyles.parentUserIconContainer}>
                        <UserIcon
                            user={answer.user}
                            size="small"
                            contextStyles={crumbStyles.parentUserIcon}
                        />
                    </span>
                )}
                <span className={crumbStyles.parentCrumbText}>{title}</span>
            </span>
        </div>
    )
}

export default ReplyModalCrumb
